import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Home, Sparkles, Eye, Palette, Check, AlertCircle } from 'lucide-react';
import { useAcademyStore } from '../../../store/academyStore';

const FILTERS = [
    { id: 'normal', name: 'Normal Vision', filter: 'none', description: 'How most people see' },
    { id: 'protanopia', name: 'Protanopia', filter: 'url(#protanopia)', description: 'Red-blind (~1% of men)' },
    { id: 'deuteranopia', name: 'Deuteranopia', filter: 'url(#deuteranopia)', description: 'Green-blind (~6% of men)' },
    { id: 'tritanopia', name: 'Tritanopia', filter: 'url(#tritanopia)', description: 'Blue-blind (rare)' },
    { id: 'achromatopsia', name: 'Achromatopsia', filter: 'grayscale(100%)', description: 'Complete color blindness' },
    { id: 'blur', name: 'Low Vision', filter: 'blur(2px)', description: 'Simulates reduced acuity' },
];

const DEMO_CARDS = [
    { title: 'Success', bg: 'bg-green-500', text: 'text-white', message: 'Payment processed!' },
    { title: 'Warning', bg: 'bg-yellow-500', text: 'text-black', message: 'Check your input' },
    { title: 'Error', bg: 'bg-red-500', text: 'text-white', message: 'Something went wrong' },
    { title: 'Info', bg: 'bg-blue-500', text: 'text-white', message: 'New update available' },
];

export const AccessibilityLens = () => {
    const { completeModule } = useAcademyStore();
    const [completed, setCompleted] = useState(false);
    const [activeFilter, setActiveFilter] = useState('normal');
    const [testedFilters, setTestedFilters] = useState<Set<string>>(new Set(['normal']));

    const handleFilterChange = (filterId: string) => {
        setActiveFilter(filterId);
        const newTested = new Set(testedFilters).add(filterId);
        setTestedFilters(newTested);

        if (newTested.size >= 4 && !completed) {
            setCompleted(true);
            completeModule('accessibility', 100, 'badge_ui-ux');
        }
    };

    const currentFilter = FILTERS.find(f => f.id === activeFilter);

    return (
        <div className="min-h-screen bg-[#f7fcf5] font-sans">
            {/* SVG Filters for Color Blindness Simulation */}
            <svg className="absolute w-0 h-0">
                <defs>
                    {/* Protanopia (Red-Blind) */}
                    <filter id="protanopia">
                        <feColorMatrix type="matrix" values="0.567, 0.433, 0, 0, 0  0.558, 0.442, 0, 0, 0  0, 0.242, 0.758, 0, 0  0, 0, 0, 1, 0" />
                    </filter>
                    {/* Deuteranopia (Green-Blind) */}
                    <filter id="deuteranopia">
                        <feColorMatrix type="matrix" values="0.625, 0.375, 0, 0, 0  0.7, 0.3, 0, 0, 0  0, 0.3, 0.7, 0, 0  0, 0, 0, 1, 0" />
                    </filter>
                    {/* Tritanopia (Blue-Blind) */}
                    <filter id="tritanopia">
                        <feColorMatrix type="matrix" values="0.95, 0.05, 0, 0, 0  0, 0.433, 0.567, 0, 0  0, 0.475, 0.525, 0, 0  0, 0, 0, 1, 0" />
                    </filter>
                </defs>
            </svg>

            {/* Header */}
            <div className="bg-white border-b border-slate-200 px-8 py-4 flex justify-between items-center sticky top-0 z-30 shadow-sm">
                <Link to="/" className="flex items-center gap-2 text-slate-500 font-bold hover:text-pink-600 transition-colors">
                    <Home size={20} /> Dashboard
                </Link>
                <div className="flex items-center gap-4">
                    {completed && <div className="hidden md:flex bg-green-100 text-green-700 px-3 py-1 rounded-full text-xs font-bold items-center gap-1"><Sparkles size={12} /> Module Complete</div>}
                    <div className="bg-pink-100 text-pink-800 px-4 py-1 rounded-full text-sm font-bold border border-pink-200">
                        👁️ The Empathy Lens
                    </div>
                </div>
            </div>

            <div className="max-w-6xl mx-auto p-8">
                <div className="text-center mb-8">
                    <h1 className="text-4xl font-black font-comic text-slate-800 mb-2 flex items-center justify-center gap-3">
                        <Eye className="text-pink-500" /> Accessibility Lens
                    </h1>
                    <p className="text-slate-600 max-w-2xl mx-auto">
                        See your designs through different eyes. Toggle filters to simulate various vision impairments and ensure your UI is accessible to everyone.
                    </p>
                </div>

                {/* Filter Buttons */}
                <div className="flex flex-wrap justify-center gap-3 mb-8">
                    {FILTERS.map(filter => (
                        <button
                            key={filter.id}
                            onClick={() => handleFilterChange(filter.id)}
                            className={`px-4 py-2 rounded-xl text-sm font-bold border-2 transition-all flex items-center gap-2 ${activeFilter === filter.id
                                ? 'bg-pink-500 text-white border-pink-500'
                                : testedFilters.has(filter.id)
                                    ? 'bg-white border-green-300 text-green-700'
                                    : 'bg-white border-slate-200 text-slate-600 hover:border-pink-300'
                                }`}
                        >
                            {testedFilters.has(filter.id) && activeFilter !== filter.id && <Check size={14} />}
                            {filter.name}
                        </button>
                    ))}
                </div>

                {/* Current Filter Info */}
                <div className="text-center mb-8">
                    <div className="inline-flex items-center gap-2 bg-slate-100 px-4 py-2 rounded-full text-sm text-slate-600">
                        <Palette size={16} />
                        <span className="font-bold">{currentFilter?.name}:</span>
                        <span>{currentFilter?.description}</span>
                    </div>
                </div>

                {/* Demo UI with Filter Applied */}
                <div
                    className="bg-white rounded-3xl p-8 shadow-xl border-2 border-slate-100"
                    style={{ filter: currentFilter?.filter || 'none' }}
                >
                    <div className="text-center mb-8">
                        <h2 className="text-2xl font-bold text-slate-800 mb-2">Sample Dashboard</h2>
                        <p className="text-slate-500">How does this UI look with the current filter?</p>
                    </div>

                    {/* Status Cards */}
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
                        {DEMO_CARDS.map((card, i) => (
                            <motion.div
                                key={card.title}
                                initial={{ opacity: 0, y: 20 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ delay: i * 0.1 }}
                                className={`${card.bg} ${card.text} p-4 rounded-2xl`}
                            >
                                <div className="text-lg font-bold mb-1">{card.title}</div>
                                <div className="text-sm opacity-90">{card.message}</div>
                            </motion.div>
                        ))}
                    </div>

                    {/* Sample Form */}
                    <div className="max-w-md mx-auto bg-slate-50 rounded-2xl p-6">
                        <div className="space-y-4">
                            <div>
                                <label className="text-sm font-bold text-slate-600 mb-1 block">Email</label>
                                <input
                                    type="email"
                                    placeholder="gorilla@jungle.com"
                                    className="w-full border-2 border-slate-200 rounded-lg px-4 py-2 focus:border-blue-500 outline-none"
                                />
                            </div>
                            <div>
                                <label className="text-sm font-bold text-slate-600 mb-1 block">Password</label>
                                <input
                                    type="password"
                                    placeholder="••••••••"
                                    className="w-full border-2 border-slate-200 rounded-lg px-4 py-2 focus:border-blue-500 outline-none"
                                />
                                <p className="text-xs text-red-500 mt-1">Password must be at least 8 characters</p>
                            </div>
                            <button className="w-full bg-green-600 hover:bg-green-500 text-white font-bold py-3 rounded-lg transition-colors">
                                Sign In
                            </button>
                            <button className="w-full bg-slate-200 hover:bg-slate-300 text-slate-700 font-bold py-3 rounded-lg transition-colors">
                                Cancel
                            </button>
                        </div>
                    </div>

                    {/* Color Chart */}
                    <div className="mt-8 flex justify-center gap-2">
                        {['red', 'orange', 'yellow', 'green', 'blue', 'purple', 'pink'].map(color => (
                            <div
                                key={color}
                                className={`w-12 h-12 rounded-xl bg-${color}-500`}
                                style={{ backgroundColor: color === 'orange' ? '#f97316' : color === 'pink' ? '#ec4899' : color === 'purple' ? '#a855f7' : undefined }}
                            />
                        ))}
                    </div>
                </div>

                {/* Tips */}
                <div className="mt-8 bg-white rounded-2xl p-6 border-2 border-slate-100">
                    <h3 className="font-bold text-slate-800 mb-3 flex items-center gap-2">
                        <AlertCircle className="text-blue-500" /> Accessibility Best Practices
                    </h3>
                    <ul className="text-slate-600 text-sm space-y-2">
                        <li>✅ Don't rely on color alone—use icons, text, and patterns</li>
                        <li>✅ Maintain high contrast (WCAG recommends 4.5:1 for text)</li>
                        <li>✅ Test with screen readers and keyboard navigation</li>
                        <li>✅ Provide alt text for images</li>
                        <li>✅ Use semantic HTML (<code>&lt;button&gt;</code>, not <code>&lt;div onClick&gt;</code>)</li>
                    </ul>
                </div>
            </div>
        </div>
    );
};
